// Named imports: the bundle carries the toolchain and MediaPipe pins, never the model list.
import { PUBLIC_MODELS_BASE } from '$env/static/public';
import { UiKeys } from '@voicedojo/backend-types';
import type { AppConfig, ModelRecord } from '@mlc-ai/web-llm';
import { t } from '$lib/i18n';
import type { Tier } from '$lib/utils/ai/inBrowserLlm';
import { catalogKey, mirrorKey } from './mirrorPaths.js';
import { mediapipe, toolchain } from './pinned-models.json';

export type CatalogModel = {
  id: string;
  displayName: string;
  tier: Tier;
  recommended: boolean;
  perfNote?: string;
  downloadMb: { f16: number; f32: number };
  license: { id: string; name: string; url: string; notice: string };
  upstream: string;
  f16: ModelRecord;
  f32: ModelRecord;
};
export type ModelCatalog = { models: CatalogModel[] };

export function mirrorBase(base: string = PUBLIC_MODELS_BASE): string {
  if (!base) throw new Error('The model mirror is not configured (PUBLIC_MODELS_BASE).');
  return base.replace(/\/+$/, '');
}

export const mirrorUrl = (source: string, base?: string): string =>
  `${mirrorBase(base)}/${mirrorKey(source)}`;

const CATALOG_STORAGE_KEY = 'vd.offline.catalog';
let catalog: Promise<ModelCatalog> | null = null;

// The last good copy keeps already-downloaded models usable offline.
export function loadModelCatalog(base?: string): Promise<ModelCatalog> {
  catalog ??= fetchCatalog(`${mirrorBase(base)}/${catalogKey({ toolchain })}`).catch((err) => {
    catalog = null;
    throw err;
  });
  return catalog;
}

async function fetchCatalog(url: string): Promise<ModelCatalog> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${url} answered ${res.status}.`);
    const fetched = (await res.json()) as ModelCatalog;
    try {
      localStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify({ url, catalog: fetched }));
    } catch {
      // Storage unavailable — the catalog still serves this page.
    }
    return fetched;
  } catch (err) {
    console.warn('[offline] fetching the model catalog failed', err);
    try {
      const cached = JSON.parse(localStorage.getItem(CATALOG_STORAGE_KEY) ?? 'null');
      if (cached?.url === url) return cached.catalog;
    } catch {
      // Storage unavailable or corrupt — no fallback.
    }
    throw new Error(t(UiKeys.Offline.CatalogUnavailable));
  }
}

export function __resetModelCatalogForTests(): void {
  catalog = null;
}

export function webLlmAppConfig({ models }: ModelCatalog, base?: string): AppConfig {
  const root = mirrorBase(base);
  return {
    model_list: models
      .flatMap((m) => [m.f16, m.f32])
      .map((record) => ({ ...record, model: `${root}/${record.model}`, model_lib: `${root}/${record.model_lib}` })),
  };
}

export function mediapipeAssets(base?: string) {
  const wasm = mediapipe.wasm as Record<string, string>;
  return {
    wasmBase: mirrorUrl(mediapipe.wasmBase, base).replace(/\/$/, ''),
    wasmIntegrity(url: string): string {
      const sri = wasm[url.slice(url.lastIndexOf('/') + 1)];
      if (!sri) throw new Error(`No pinned integrity for ${url}.`);
      return sri;
    },
    segmenter: { url: mirrorUrl(mediapipe.segmenter.source, base), integrity: mediapipe.segmenter.integrity },
  };
}
